import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, message, Space, Popconfirm, Typography, Upload, Grid, Card } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts, addProduct, updateProduct, deleteProduct } from '../../store/productsSlice';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { API_URL } from '../../utils/api';
import { exportToExcel } from '../../utils/exportUtils';
import { formatCurrency } from '../../utils/formatters';

const { Title } = Typography;

const Products = () => {
  const dispatch = useDispatch();
  const { products, loading } = useSelector((state) => state.products);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  const handleAdd = () => {
    setEditingProduct(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingProduct(record);
    // Exclude image from form values so Upload component is empty by default
    const { image, ...rest } = record;
    form.setFieldsValue(rest);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteProduct(id)).unwrap();
      message.success('Product deleted successfully');
    } catch (err) {
      message.error('Failed to delete product: ' + (err.message || 'Unknown error'));
    }
  };

  const handleModalOk = () => {
    form.validateFields().then(async (values) => {
      try {
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('price', values.price);
        formData.append('ml', values.ml);
        formData.append('stock', values.stock || 0);
        formData.append('discount', values.discount || 0);
        formData.append('discountedPrice', values.discountedPrice || values.price);
        formData.append('description', values.description || '');

        // Check if a file was uploaded
        if (values.image && values.image.file) {
          formData.append('image', values.image.file);
        } else if (values.image && values.image.fileList && values.image.fileList.length > 0) {
          formData.append('image', values.image.fileList[0].originFileObj);
        }

        if (editingProduct) {
          await dispatch(updateProduct({ id: editingProduct._id, data: formData })).unwrap();
          message.success('Product updated successfully');
        } else {
          await dispatch(addProduct(formData)).unwrap();
          message.success('Product added successfully');
        }
        setIsModalVisible(false);
        form.resetFields();
      } catch (err) {
        // Handle validation errors from backend or network errors
        const errorMsg = err.message || (typeof err === 'string' ? err : 'Unknown error occurred');
        message.error(`Operation failed: ${errorMsg}`);
        console.error('Product operation error:', err);
      }
    });
  };

  const handleExport = () => {
    if (!products || products.length === 0) {
      message.warning('No products to export');
      return;
    }

    const dataToExport = products.map(p => ({
      ID: p._id,
      Name: p.name,
      'Price (₹)': p.price,
      'Discount (%)': p.discount || 0,
      'Discounted Price (₹)': p.discountedPrice || p.price,
      ML: p.ml,
      Stock: p.stock || 0,
      Image: p.image || 'N/A'
    }));

    exportToExcel(dataToExport, 'Products_List');
    message.success('Products exported successfully');
  };

  const screens = Grid.useBreakpoint();

  const columns = [
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      render: (text) => {
        if (!text) return 'No Image';
        const src = text.startsWith('http') ? text : `${API_URL}${text}`;
        return <img src={src} alt="product" style={{ width: 50, height: 50, objectFit: 'cover' }} />;
      },
    },
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Price (₹)',
      dataIndex: 'price',
      key: 'price',
      render: (price) => formatCurrency(price),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: 'ML',
      dataIndex: 'ml',
      key: 'ml',
      sorter: (a, b) => a.ml - b.ml,
    },
    {
      title: 'In Stock',
      dataIndex: 'stock',
      key: 'stock',
      sorter: (a, b) => (a.stock || 0) - (b.stock || 0),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Delete product"
            description="Are you sure to delete this product?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 16 }}>
        <Title level={2}>Products</Title>
        <Space>
          <Button icon={<FileExcelOutlined />} onClick={handleExport}>
            Export to Excel
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Product
          </Button>
        </Space>
      </div>

      {!screens.md ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {products.map(product => {
            const imgSrc = product.image ? (product.image.startsWith('http') ? product.image : `${API_URL}${product.image}`) : null;
            return (
              <Card
                key={product._id}
                hoverable
                size="small"
                cover={imgSrc ? <img alt={product.name} src={imgSrc} style={{ height: 120, objectFit: 'cover' }} /> : <div style={{ height: 120, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}>No Image</div>}
                actions={[
                  <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(product)} />,
                  <Popconfirm
                    title="Delete product"
                    description="Are you sure?"
                    onConfirm={() => handleDelete(product._id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                ]}
              >
                <Card.Meta
                  title={<div style={{ fontSize: 14 }}>{product.name}</div>}
                  description={
                    <div>
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>{formatCurrency(product.price)}</div>
                      <div style={{ fontSize: 12 }}>{product.ml} ML • Stock: {product.stock || 0}</div>
                    </div>
                  }
                />
              </Card>
            );
          })}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={products}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 'max-content' }}
        />
      )}

      <Modal
        title={editingProduct ? 'Edit Product' : 'Add Product'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: 'Please enter product name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Current Image">
            {editingProduct && editingProduct.image ? (
              <img
                src={editingProduct.image.startsWith('http') ? editingProduct.image : `${API_URL}${editingProduct.image}`}
                alt="Current"
                style={{ width: 100, height: 100, objectFit: 'cover', marginBottom: 10 }}
              />
            ) : <Typography.Text type="secondary">No image set</Typography.Text>}
          </Form.Item>

          <Form.Item
            name="image"
            label="Upload Image"
            rules={[{ required: !editingProduct, message: 'Please upload an image' }]} // Required only for new products? Or optional?
          >
            <Upload
              maxCount={1}
              beforeUpload={() => false}
              listType="picture"
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            name="price"
            label="Price"
            rules={[{ required: true, message: 'Please enter price' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} prefix="₹" />
          </Form.Item>

          <Form.Item
            name="stock"
            label="In Stock"
            rules={[{ required: true, message: 'Please enter stock quantity' }]}
            initialValue={0}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>

          <Form.Item
            name="discount"
            label="Discount (%)"
            rules={[{ required: false }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              formatter={value => `${value}%`}
              parser={value => value.replace('%', '')}
              onChange={(value) => {
                const price = form.getFieldValue('price') || 0;
                const discount = value || 0;
                const discounted = price - (price * discount / 100);
                form.setFieldsValue({ discountedPrice: discounted.toFixed(2) });
              }}
            />
          </Form.Item>

          <Form.Item
            name="discountedPrice"
            label="After Discount Price"
          >
            <InputNumber
              style={{ width: '100%' }}
              prefix="₹"
              readOnly
              disabled
            />
          </Form.Item>

          <Form.Item
            name="ml"
            label="ML"
            rules={[{ required: true, message: 'Please enter ML' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>

          <Form.Item
            name="description"
            label="Product Description"
          >
            <Input.TextArea rows={4} placeholder="Enter product details..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Products;
